import type { AxiosResponse } from "axios";
import addMinutes from "date-fns/addMinutes";
import subMinutes from "date-fns/subMinutes";
import { ref } from "vue";

import { $streemApiV1 } from "shared/boot/api";
import { dateToTimestamp, parseDate } from "shared/helpers/date";
import DateRange from "shared/helpers/DateRange";
import type ApiClient from "shared/services/api/ApiClient";
import type {
  RadioClip,
  TvCaption,
  TvClip,
  TvFragment,
  TvLogoAppearance,
  TvSuper,
  TvThumbnail,
} from "shared/types/mentions";

interface TranscriptConfig {
  minutesBefore?: number;
  minutesAfter?: number;
  limit?: number;
}

interface UseTranscriptDataProps {
  mention: TranscriptMention;
  api?: ApiClient;
  minutesBefore?: number;
  minutesAfter?: number;
  limit?: number;
}

export type TranscriptMention =
  | TvCaption
  | TvClip
  | TvSuper
  | TvLogoAppearance
  | RadioClip;

interface TranscriptParams {
  after: number;
  before: number;
  limit: number;
}

const DEFAULT_CONFIG: Required<TranscriptConfig> = {
  minutesBefore: 30,
  minutesAfter: 30,
  limit: 3600,
};

export function useTranscriptData({
  mention,
  api = $streemApiV1,
  minutesBefore = DEFAULT_CONFIG.minutesBefore,
  minutesAfter = DEFAULT_CONFIG.minutesAfter,
  limit = DEFAULT_CONFIG.limit,
}: UseTranscriptDataProps) {
  const fragments = ref<TvFragment[]>([]);
  const thumbnails = ref<TvThumbnail[]>([]);
  const range = ref<DateRange | null>(null);
  const isLoading = ref(false);
  const hasLoaded = ref(false);

  const getTranscriptParams = (): TranscriptParams => {
    const startTime = dateToTimestamp(
      subMinutes(parseDate(mention.start_time), minutesBefore)
    );

    const endTime = dateToTimestamp(
      addMinutes(parseDate(mention.start_time), minutesAfter)
    );

    // @ts-expect-error - not typesafe: startTime/endTime are numbers but fromDates expects Date.
    range.value = DateRange.fromDates(startTime, endTime);

    return {
      after: startTime,
      before: endTime,
      limit,
    };
  };

  const loadFragments = (
    params: TranscriptParams
  ): Promise<AxiosResponse<TvFragment[]>> =>
    api.get(`tv_channels/${mention.source.id}/tv_caption_fragments`, {
      params,
    });

  const loadThumbnails = (
    params: TranscriptParams
  ): Promise<AxiosResponse<TvThumbnail[]>> =>
    api.get(`tv_channels/${mention.source.id}/thumbnails`, {
      params,
    });

  const fetchTranscriptData = async (): Promise<void> => {
    isLoading.value = true;
    hasLoaded.value = false;

    try {
      const transcriptParams = getTranscriptParams();

      const [fragmentsResponse, thumbnailsResponse] = await Promise.all([
        loadFragments(transcriptParams),
        loadThumbnails(transcriptParams),
      ]);

      fragments.value = fragmentsResponse.data;
      thumbnails.value = thumbnailsResponse.data;
    } finally {
      isLoading.value = false;
      hasLoaded.value = true;
    }
  };

  const resetState = () => {
    fragments.value = [];
    hasLoaded.value = false;
    isLoading.value = false;
    range.value = null;
    thumbnails.value = [];
  };

  return {
    fragments,
    hasLoaded,
    isLoading,
    range,
    thumbnails,

    fetchTranscriptData,
    resetState,
  };
}
